import { Trans } from "react-i18next";
import i18nPackage from "../../i18n/i18n";
import StepDescription from "../edit-quiz-steps/CommonStepComponents";
import styles from "./AccommodationTable.module.css";
import {
	BaseListContainer,
	BaseListFooterContainer,
	BaseListHeaderContainer,
	BaseListInfoArea,
	BaseListTitle,
	EditQuizNoResults,
} from "../proctoring-options/option-lists/OptionListsComponents";
import {
	CoreAvatar,
	CoreBackButton,
	CoreButton,
	CoreCheckbox,
	CoreChip,
	CoreDataView,
	CoreDataViewColumn,
	CoreInfoIcon,
	CoreMenuButton,
	CoreMenuItem,
	CorePagination,
	CoreSearchField,
	CoreSkeletonWrapper,
	CoreTable,
	CoreTableColumn,
	OverflowText,
	SortConfig,
} from "../../core";
import AccommodationIcon from "../images/confirmation/accommodation-step.svg?react";
import {
	ACCOMMODATION_FILTER,
	AccommodationFilterType,
	getProfileColor,
	UseAccommodationFilterReturn,
	UseExemptionModalsReturn,
	usePaginatedRows,
	UseStudentSelectionReturn,
} from "./exemptionHelper";
import EditQuizFilter from "../components/EditQuizFilter";
import { SettingsToolbar, settingsToolbarButtonClassName, settingsToolbarFieldClassName } from "../../common/settings";
import { ListItemText, Tooltip, Typography } from "@mui/material";
import { useCallback, useId, useMemo, useState } from "react";
import { ExemptedStudent } from "../../types/common";
import { ConditionalWrapper, TooltipWrapper } from "../../wrapper";
import { ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { getProfileName, useMobileLayout } from "../..";

type AccommodationStatusCellProps = {
	isExempted: boolean;
	proctoringOptionProfileId: ExemptedStudent["proctoringOptionProfileId"];
} & Pick<AccommodationTableProps, "profilesMap">;

function AccommodationStatusCell({ isExempted, proctoringOptionProfileId, profilesMap }: AccommodationStatusCellProps) {
	const profileName =
		isExempted && proctoringOptionProfileId
			? getProfileName(profilesMap.get(proctoringOptionProfileId))
			: getProfileName(undefined);

	const profileColor = getProfileColor(profileName);

	if (!isExempted)
		return (
			<CoreChip
				chipColor="#eeeeee"
				fontColor="#4c4c4c"
				variant="rectangular"
				className={styles.noAccommodationBadge}
				tooltip={i18nPackage.t("editQuizPage.accommodationStep.noAccommodationTooltip")}
				tooltipProps={{
					placement: "top",
				}}
			>
				{i18nPackage.t("editQuizPage.accommodationStep.noAccommodation")}
			</CoreChip>
		);

	return isExempted && proctoringOptionProfileId ? (
		<CoreChip
			variant="rectangular"
			tooltip={i18nPackage.t("editQuizPage.accommodationStep.modifiedProctoringTooltip", { profileName })}
			chipColor={profileColor}
			className={styles.profileBadgeTag}
			tooltipProps={{
				placement: "top",
			}}
		>
			{profileName}
		</CoreChip>
	) : (
		<CoreChip
			chipColor="#F9E4F5"
			variant="rectangular"
			className={styles.notProctoredBadge}
			tooltip={i18nPackage.t("editQuizPage.accommodationStep.notProctoredTooltip")}
			tooltipProps={{
				placement: "top",
			}}
		>
			{i18nPackage.t("editQuizPage.accommodationStep.notProctored")}
		</CoreChip>
	);
}

type NameCellProps = {
	student: ExemptedStudent;
	isMobileLayout: boolean;
	showAvatar?: boolean;
};

function NameCell({ student, isMobileLayout, showAvatar = true }: NameCellProps) {
	const { name, userId } = student;
	return (
		<div className={styles.nameCell}>
			{showAvatar && <CoreAvatar colorConfig={userId}>{name[0]}</CoreAvatar>}
			<ConditionalWrapper
				condition={isMobileLayout}
				whenTrue={(children) => <Typography>{children}</Typography>}
				whenFalse={(children) => <OverflowText>{children}</OverflowText>}
			>
				{name}
			</ConditionalWrapper>
		</div>
	);
}

type StudentActionMenuProps = {
	student: ExemptedStudent;
	actions: Array<{
		name: (student: ExemptedStudent) => string;
		hide?: boolean | ((student: ExemptedStudent) => boolean);
		action: (student: ExemptedStudent) => void;
		ariaHasPopup?: boolean;
	}>;
};

function StudentActionMenu({ student, actions }: StudentActionMenuProps) {
	const [menuShowing, setMenuShowing] = useState(false);

	// Don't show menu button if every menu action is hidden
	if (actions.every((a) => typeof a.hide === "function" && a.hide(student))) return null;

	return (
		<CoreMenuButton
			menuShowing={menuShowing}
			setMenuShowing={setMenuShowing}
			aria-label={i18nPackage.t("editQuizPage.accommodationStep.actionsAriaLabel")}
			className={styles.actionMenu}
		>
			{actions.map((action) =>
				(typeof action.hide === "function" ? action.hide(student) : action.hide) ? null : (
					<CoreMenuItem
						key={action.name(student)}
						onClick={() => {
							action.action(student);
							setMenuShowing(false);
						}}
						aria-haspopup={action.ariaHasPopup}
					>
						<ListItemText primary={action.name(student)} />
					</CoreMenuItem>
				),
			)}
		</CoreMenuButton>
	);
}

type TableToolbarProps = {
	numSelected: number;
	onUpdateAccommodation?: () => void;
	onRemoveAccommodation?: () => void;
	isAddAccommodation?: boolean;
};

function TableToolbar({
	numSelected,
	onUpdateAccommodation,
	onRemoveAccommodation,
	isAddAccommodation,
}: TableToolbarProps) {
	return (
		<div className={styles.tableToolbar}>
			<Typography className={styles.selected}>
				{i18nPackage.t("editQuizPage.accommodationStep.selectedTestTakers", { count: numSelected })}
			</Typography>
			<div className={styles.buttonWrapper}>
				{onUpdateAccommodation && (
					<CoreButton onClick={onUpdateAccommodation} aria-haspopup="dialog">
						{isAddAccommodation
							? i18nPackage.t("editQuizPage.accommodationStep.addAccommodation")
							: i18nPackage.t("editQuizPage.accommodationStep.updateAccommodation")}
					</CoreButton>
				)}
				{onRemoveAccommodation && (
					<CoreButton onClick={onRemoveAccommodation} color="cancel">
						{i18nPackage.t("editQuizPage.accommodationStep.removeAccommodation")}
					</CoreButton>
				)}
			</div>
		</div>
	);
}

/** The lead paragraph above the roster, reused by callers that render it in their own chrome. */
export function getAccommodationsDescription(): string {
	return i18nPackage.t("editQuizPage.accommodationStep.accommodationsHeader");
}

type AccommodationToolbarProps = {
	onSearch: (value: string) => void;
	ariaControls: string;
	totalResults: number;
	searchFilter: string;
	activeStatus: AccommodationFilterType;
	onFilterChange: (status: AccommodationFilterType) => void;
	presentation?: AccommodationTablePresentation;
};

function AccommodationToolbar({
	activeStatus,
	ariaControls,
	onFilterChange,
	onSearch,
	totalResults,
	searchFilter,
	presentation = "default",
}: AccommodationToolbarProps) {
	const settingsPresentation = presentation === "settings";

	const filter = (
		<EditQuizFilter<AccommodationFilterType>
			value={activeStatus}
			onChange={onFilterChange}
			minWidth={220}
			ariaLabel={i18nPackage.t("editQuizPage.accommodationStep.filter.ariaLabel")}
			renderValue={(value) => {
				return (
					<Typography component={"span"} className={styles.filterValue}>
						{i18nPackage.t(`editQuizPage.accommodationStep.filter.${value}`)}
					</Typography>
				);
			}}
		>
			{ACCOMMODATION_FILTER.map((val) => {
				return (
					<CoreMenuItem value={val} key={val}>
						<ListItemText
							className={styles.filterOption}
							primary={i18nPackage.t(`editQuizPage.accommodationStep.filter.${val}`)}
						/>
					</CoreMenuItem>
				);
			})}
		</EditQuizFilter>
	);

	if (settingsPresentation) {
		return (
			<SettingsToolbar>
				<CoreSearchField
					autoComplete="off"
					noForm
					label={i18nPackage.t("editQuizPage.accommodationStep.searchPlaceholder")}
					onSearch={onSearch}
					ariaControls={ariaControls}
					totalResults={totalResults}
					value={searchFilter}
					fullWidth
					debounceSearch={250}
					searchFieldWrapperClassname={settingsToolbarFieldClassName}
				/>
				{filter}
			</SettingsToolbar>
		);
	}

	return (
		<div className={styles.searchToolbarWrapper}>
			<CoreSearchField
				autoComplete="off"
				noForm
				label={i18nPackage.t("editQuizPage.accommodationStep.searchPlaceholder")}
				onSearch={onSearch}
				ariaControls={ariaControls}
				totalResults={totalResults}
				value={searchFilter}
			/>
			{filter}
		</div>
	);
}

export type AccommodationTablePresentation = "default" | "settings";

export type AccommodationTableProps = {
	loading?: boolean;
	curPage: number;
	onCurPageChange: (page: number) => void;
	rosterControls: UseAccommodationFilterReturn;
	studentSelectControls: UseStudentSelectionReturn;
	modalControls: UseExemptionModalsReturn;
	clearRecentIds: () => void;
	editInstitutionExemptions?: boolean;
	profilesMap: Map<number, ProctoringOptionProfileV2>;
	onBack?: () => void;
	backButtonText?: string;
	onRemoveAccommodation: (student?: ExemptedStudent) => void;
	presentation?: AccommodationTablePresentation;
};

export default function AccommodationTable({
	loading,
	curPage,
	onCurPageChange,
	rosterControls,
	studentSelectControls,
	clearRecentIds,
	editInstitutionExemptions,
	profilesMap,
	modalControls: { accommodationModal, viewModal },
	backButtonText = i18nPackage.t("editQuizPage.accommodationStep.backButton"),
	onBack,
	onRemoveAccommodation,
	presentation = "default",
}: AccommodationTableProps) {
	const controlId = useId();
	const isMobileLayout = useMobileLayout();
	const settingsPresentation = presentation === "settings";

	const { activeStatus, filteredRoster, setSortConfig, sortConfig, searchFilter, setActiveStatus, setSearchFilter } =
		rosterControls;
	const { paginatedRows, totalPages } = usePaginatedRows(filteredRoster, curPage, onCurPageChange);
	const hasSelectableRows = filteredRoster.some((student) => student.type !== "institution");

	const {
		allSelected,
		isAllSelectedNoAccommodation,
		isIndeterminate,
		selectedIds,
		toggleSelectOne,
		handleSelectAll,
		isStudentExempted,
		canRemoveAccommodations,
		selectedStudents,
	} = studentSelectControls;

	const handleSortConfig = (newSort: SortConfig<ExemptedStudent>[]) => {
		setSortConfig(newSort);
		clearRecentIds();
	};

	const handleSearch = useCallback(
		(value: string) => {
			setSearchFilter(value.toLowerCase());
			onCurPageChange(1);
			clearRecentIds();
		},
		[clearRecentIds, onCurPageChange, setSearchFilter],
	);

	const handleFilterChange = useCallback(
		(status: AccommodationFilterType) => {
			setActiveStatus(status);
			onCurPageChange(1);
			clearRecentIds();
		},
		[clearRecentIds, onCurPageChange, setActiveStatus],
	);

	const studentActions = useMemo(
		(): StudentActionMenuProps["actions"] => [
			{
				name: () => i18nPackage.t("editQuizPage.accommodationStep.menu.view"),
				action: viewModal.open,
				hide: (student) => !isStudentExempted(student.userId),
				ariaHasPopup: true,
			},
			{
				name: (student) =>
					isStudentExempted(student.userId)
						? i18nPackage.t("editQuizPage.accommodationStep.menu.edit")
						: i18nPackage.t("editQuizPage.accommodationStep.menu.add"),
				action: (student) => {
					toggleSelectOne(String(student.userId), true);
					accommodationModal.open();
				},
				hide: (student) => {
					return !editInstitutionExemptions && student.type === "institution";
				},
				ariaHasPopup: true,
			},
			{
				name: () => i18nPackage.t("editQuizPage.accommodationStep.menu.remove"),
				action: (student) => {
					onRemoveAccommodation(student);
					toggleSelectOne(String(student.userId), false);
				},
				hide: (student) => {
					return (!editInstitutionExemptions && student.type === "institution") || !isStudentExempted(student.userId);
				},
			},
		],
		[
			accommodationModal,
			editInstitutionExemptions,
			isStudentExempted,
			onRemoveAccommodation,
			toggleSelectOne,
			viewModal.open,
		],
	);

	const columns = useMemo(
		(): CoreDataViewColumn<ExemptedStudent>[] => [
			{
				key: "userId",
				header: (
					<CoreCheckbox
						checked={allSelected}
						onChange={(e) => handleSelectAll(e.target.checked)}
						inputProps={{
							"aria-label": allSelected
								? i18nPackage.t("editQuizPage.accommodationStep.unselectAll")
								: i18nPackage.t("editQuizPage.accommodationStep.selectAll"),
						}}
						disabled={!hasSelectableRows}
						indeterminate={isIndeterminate}
					/>
				),
				cell: (student) => {
					const checked = selectedIds.has(String(student.userId));
					const ariaKey = checked ? "unselectStudent" : "selectStudent";
					return (
						<TooltipWrapper
							condition={student.type === "institution" && !editInstitutionExemptions}
							title={i18nPackage.t("editQuizPage.accommodationStep.institutionToolTip")}
						>
							<CoreCheckbox
								disabled={student.type === "institution" && !editInstitutionExemptions}
								onChange={(e) => toggleSelectOne(String(student.userId), e.target.checked)}
								checked={checked}
								aria-label={i18nPackage.t(`editQuizPage.accommodationStep.${ariaKey}`, { student: student.name })}
							/>
						</TooltipWrapper>
					);
				},
				width: "50px",
			},
			{
				key: "name",
				header: i18nPackage.t("editQuizPage.accommodationStep.testTaker"),
				cell: (student) => <NameCell student={student} isMobileLayout={isMobileLayout} />,
				sortable: true,
				allowOverflow: isMobileLayout,
			},
			{
				key: "proctoringOptionProfileId",
				header: i18nPackage.t("editQuizPage.accommodationStep.accommodationStatus"),
				cell: (student) => (
					<AccommodationStatusCell
						isExempted={isStudentExempted(student.userId)}
						proctoringOptionProfileId={student.proctoringOptionProfileId}
						profilesMap={profilesMap}
					/>
				),
				sortable: true,
				allowOverflow: isMobileLayout,
			},
			{
				key: "selection" as never,
				width: "50px",
				header: i18nPackage.t("editQuizPage.accommodationStep.actionsHeader"),
				cell: (student) => <StudentActionMenu student={student} actions={studentActions} />,
			},
		],
		[
			allSelected,
			editInstitutionExemptions,
			handleSelectAll,
			hasSelectableRows,
			isIndeterminate,
			isMobileLayout,
			isStudentExempted,
			profilesMap,
			selectedIds,
			studentActions,
			toggleSelectOne,
		],
	);

	const settingsColumns = useMemo(
		(): CoreTableColumn<ExemptedStudent>[] => [
			{
				variant: "selection",
				key: "userId",
				header: (
					<CoreCheckbox
						checked={allSelected}
						onChange={(e) => handleSelectAll(e.target.checked)}
						inputProps={{
							"aria-label": allSelected
								? i18nPackage.t("editQuizPage.accommodationStep.unselectAll")
								: i18nPackage.t("editQuizPage.accommodationStep.selectAll"),
						}}
						disabled={!hasSelectableRows}
						indeterminate={isIndeterminate}
					/>
				),
				cell: (row) => {
					const student = row.data;
					const checked = selectedIds.has(String(student.userId));
					const ariaKey = checked ? "unselectStudent" : "selectStudent";
					return (
						<TooltipWrapper
							condition={student.type === "institution" && !editInstitutionExemptions}
							title={i18nPackage.t("editQuizPage.accommodationStep.institutionToolTip")}
						>
							<CoreCheckbox
								disabled={student.type === "institution" && !editInstitutionExemptions}
								onChange={(e) => toggleSelectOne(String(student.userId), e.target.checked)}
								checked={checked}
								aria-label={i18nPackage.t(`editQuizPage.accommodationStep.${ariaKey}`, { student: student.name })}
							/>
						</TooltipWrapper>
					);
				},
			},
			{
				key: "name",
				header: i18nPackage.t("editQuizPage.accommodationStep.testTaker"),
				cell: (row) => <NameCell student={row.data} isMobileLayout={isMobileLayout} showAvatar={false} />,
				minWidth: 220,
				descendingComparator: (a, b) => b.name.localeCompare(a.name),
			},
			{
				key: "proctoringOptionProfileId",
				header: i18nPackage.t("editQuizPage.accommodationStep.accommodationStatus"),
				cell: (row) => (
					<AccommodationStatusCell
						isExempted={isStudentExempted(row.data.userId)}
						proctoringOptionProfileId={row.data.proctoringOptionProfileId}
						profilesMap={profilesMap}
					/>
				),
				minWidth: 240,
				descendingComparator: (a, b) => {
					const getStatusSortValue = (student: ExemptedStudent) => {
						const isExempted = isStudentExempted(student.userId);
						if (!isExempted) return "0";
						if (!student.proctoringOptionProfileId) return "1";
						const profile = profilesMap.get(student.proctoringOptionProfileId);
						return `2_${getProfileName(profile)}`;
					};
					return getStatusSortValue(b).localeCompare(getStatusSortValue(a));
				},
			},
		],
		[
			allSelected,
			editInstitutionExemptions,
			handleSelectAll,
			hasSelectableRows,
			isIndeterminate,
			isMobileLayout,
			isStudentExempted,
			profilesMap,
			selectedIds,
			toggleSelectOne,
		],
	);

	if (settingsPresentation) {
		const accommodationDisclaimer = new DOMParser().parseFromString(
			i18nPackage.t("editQuizPage.accommodationStep.accommodationsDisclaimer"),
			"text/html",
		).body.textContent;
		const selectedStudentForView =
			selectedStudents.length === 1 && isStudentExempted(selectedStudents[0].userId) ? selectedStudents[0] : undefined;

		return (
			<div className={`${styles.accommodationsPage} ${styles.settingsAccommodationsPage}`}>
				<CoreSkeletonWrapper loading={loading}>
					<Typography component="p" className={styles.settingsDescription}>
						{getAccommodationsDescription()}
						<Tooltip
							title={
								<Trans
									i18n={i18nPackage}
									i18nKey={"editQuizPage.accommodationStep.accommodationsDisclaimer"}
									components={{ b: <strong /> }}
								/>
							}
							placement="top"
							describeChild
						>
							<span className={styles.settingsDescriptionTooltip} tabIndex={0} aria-label={accommodationDisclaimer}>
								<CoreInfoIcon width={18} height={18} color="#5f6368" />
							</span>
						</Tooltip>
					</Typography>
				</CoreSkeletonWrapper>
				<CoreSkeletonWrapper loading={loading}>
					<AccommodationToolbar
						activeStatus={activeStatus}
						ariaControls={controlId}
						onFilterChange={handleFilterChange}
						onSearch={handleSearch}
						searchFilter={searchFilter}
						totalResults={filteredRoster.length}
						presentation="settings"
					/>
				</CoreSkeletonWrapper>
				{selectedIds.size > 0 && (
					<>
						<Typography component="p" className={styles.settingsSelected}>
							{i18nPackage.t("editQuizPage.accommodationStep.selectedTestTakers", { count: selectedIds.size })}
						</Typography>
						<SettingsToolbar>
							{selectedStudentForView && (
								<CoreButton
									type="button"
									color="secondary"
									className={settingsToolbarButtonClassName}
									onClick={() => viewModal.open(selectedStudentForView)}
									aria-haspopup="dialog"
								>
									{i18nPackage.t("editQuizPage.accommodationStep.menu.view")}
								</CoreButton>
							)}
							<CoreButton
								type="button"
								className={settingsToolbarButtonClassName}
								onClick={accommodationModal.open}
								aria-haspopup="dialog"
							>
								{isAllSelectedNoAccommodation
									? i18nPackage.t("editQuizPage.accommodationStep.addAccommodation")
									: i18nPackage.t("editQuizPage.accommodationStep.updateAccommodation")}
							</CoreButton>
							{canRemoveAccommodations && (
								<CoreButton
									type="button"
									color="cancel"
									className={settingsToolbarButtonClassName}
									onClick={() => onRemoveAccommodation()}
								>
									{i18nPackage.t("editQuizPage.accommodationStep.removeAccommodation")}
								</CoreButton>
							)}
						</SettingsToolbar>
					</>
				)}
				<CoreTable
					variant="settings"
					data={filteredRoster}
					columns={settingsColumns}
					rowsPerPage={8}
					paginationSize="small"
					tableMinWidth={640}
					initialSortKey="name"
					initialSortOrder="asc"
					sortRequired
					resetPageKey={`${activeStatus} ${searchFilter}`}
					loading={loading}
					id={controlId}
					noResultsTitle={i18nPackage.t("editQuizPage.components.noResultsTitle")}
					noResultsString={i18nPackage.t("editQuizPage.components.noResultsDesc")}
				/>
			</div>
		);
	}

	return (
		<div className={styles.accommodationsPage}>
			{onBack && (
				<CoreSkeletonWrapper loading={loading}>
					<div className={styles.backButtonWrapper}>
						<div className={styles.sideBlock}>
							<CoreBackButton onBack={onBack} text={backButtonText} />{" "}
						</div>
						<Typography variant="h2" className={styles.title}>
							{i18nPackage.t("editQuizPage.accommodationStep.manageAccommodations")}
						</Typography>
						<div className={styles.sideBlock} aria-hidden="true" />
					</div>
				</CoreSkeletonWrapper>
			)}
			<StepDescription loading={loading}>{getAccommodationsDescription()}</StepDescription>
			<StepDescription loading={loading}>
				<Trans
					i18n={i18nPackage}
					i18nKey={"editQuizPage.accommodationStep.accommodationsDisclaimer"}
					components={{ b: <strong /> }}
				/>
			</StepDescription>
			<BaseListContainer>
				<CoreSkeletonWrapper loading={loading}>
					<BaseListHeaderContainer>
						<BaseListInfoArea
							title={
								<BaseListTitle fontSize={"0.9375rem !important"} variant="h2">
									{i18nPackage.t("editQuizPage.accommodationStep.availableTestTakers")}
								</BaseListTitle>
							}
							icon={<AccommodationIcon />}
						/>
						<AccommodationToolbar
							activeStatus={activeStatus}
							ariaControls={controlId}
							onFilterChange={handleFilterChange}
							onSearch={handleSearch}
							searchFilter={searchFilter}
							totalResults={filteredRoster.length}
						/>
						{selectedIds.size > 0 && (
							<TableToolbar
								numSelected={selectedIds.size}
								isAddAccommodation={isAllSelectedNoAccommodation}
								onUpdateAccommodation={accommodationModal.open}
								onRemoveAccommodation={canRemoveAccommodations ? () => onRemoveAccommodation() : undefined}
							/>
						)}
					</BaseListHeaderContainer>
				</CoreSkeletonWrapper>
				<BaseListFooterContainer>
					<CoreDataView
						data={paginatedRows}
						columns={columns}
						loading={loading}
						id={controlId}
						disableInternalSorting
						sortConfig={sortConfig}
						onSortChange={handleSortConfig}
						renderers={{
							noResults: <EditQuizNoResults />,
						}}
						slots={{
							tableBodyCell: {
								className: styles.accommodationCell,
							},
							tableHeadCell: {
								className: styles.accommodationCell,
							},
							table: {
								className: styles.accommodationTable,
							},
							tableBodyRow: {
								className: styles.accommodationRow,
							},
						}}
					/>
					{totalPages > 1 && (
						<CorePagination
							count={totalPages}
							onChange={(_e, page) => onCurPageChange(page)}
							defaultPage={1}
							size={"medium"}
							page={curPage}
							paginationWrap={false}
						/>
					)}
				</BaseListFooterContainer>
			</BaseListContainer>
		</div>
	);
}
